/* eslint-disable import/no-unresolved */
import moment from "moment";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Yup from "yup";

import { Box, Button, Form, Spinner, Text, useMediaQuery } from "@ui-kit/components/index.es";

import CreateTemplateModal from "pages/v2/transfer/CreateTemplateModal";
import TADWarningModal from "pages/v2/transfer/TADWarningModal";
import { getBoolean } from "util/config";

import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { actions as transactionLinesActions } from "reducers/form/transactionLines";
import { actions as holidaysActions } from "reducers/holidays";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as templateActions, selectors as templateSelectors } from "reducers/template";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";

import * as configUtils from "util/config";
import * as dateUtils from "util/date";
import * as i18n from "util/i18n";
import {
    buildInternalCreditAccountOptions,
    isInternalCreditCvu,
    resolveInternalCreditAccount,
    resolveInternalSendActivityId,
    sanitizeInternalCreditWhenOwnDisabled,
} from "util/internalTransferCreditAccount";
import * as schedulerUtils from "util/scheduler";
import { saveLocation } from "util/transaction";

import { replace } from "react-router-redux";
import { selectors as i18nSelectors } from "reducers/i18n";
import { numberFormat } from "util/number";
import { isUxTransfersImprovementsEnabled, isUxTransfersOwnImprovementsEnabled } from "util/v3ShellLayoutSync";
import { resolveTransferAccountRef } from "v3/pages/transfers/logic/transferExecuteShared";
import { isTransferInternalContinueInactive } from "v3/pages/transfers/logic/transferExecuteValidation";
import Limits from "./Limits";
import Message from "./scheduler/Message";
import SchedulerModal from "./scheduler/SchedulerModal";
import TemplateDrawer from "./TemplateDrawer";

const MAX_REQUIRED = "22";
const ID_FORM = "transferInternal";

export { getTransferInternalTitleI18nKey, getTransferInternalTitleLabel } from "util/transferInternalTitleLabels";

function TransferInternalForm(props) {
    TransferInternalForm.propTypes = {
        fetching: bool.isRequired,
        fetchingDraft: bool.isRequired,
        fetchingSubmit: bool.isRequired,
        dispatch: func.isRequired,
        onCreateTemplate: func.isRequired,
        isDesktop: bool.isRequired,
        accounts: arrayOf(
            shape({
                idProduct: string.isRequired,
            }),
        ).isRequired,
        externalOwnAccounts: arrayOf(shape({})),
        limits: arrayOf(shape({})).isRequired,
        limit: number.isRequired,
        limitUsed: number.isRequired,
        summary: shape({}).isRequired,
        agendaVisible: bool.isRequired,
        templates: shape({}).isRequired,
        activeEnvironment: shape({
            permissions: shape({
                transferInternal: bool,
                transferThirdParties: bool,
            }),
        }).isRequired,
        isRetailEnvironment: bool.isRequired,
        isCVU: bool.isRequired,
        hideAmount: bool.isRequired,
        isTemplatesVisible: bool.isRequired,
        setIsTemplatesVisible: func.isRequired,
        location: string.isRequired,
        setAgendaVisible: func.isRequired,
        lang: string.isRequired,
        idTransaction: string.isRequired,
    };

    TransferInternalForm.defaultProps = {
        externalOwnAccounts: [],
    };

    const {
        dispatch,
        isDesktop,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        location,
        accounts,
        externalOwnAccounts = [],
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        summary,
        limit,
        limitUsed,
        onCreateTemplate,
        agendaVisible,
        setAgendaVisible,
        isCVU,
        lang,
        hideAmount,
        limits,
    } = props;

    const [values, setValues] = useState({
        debitAccount: undefined,
        creditAccount: undefined,
        amount: 0,
        currency: 0,
        sendEmail: false,
        reference: "",
        email: "",
        emailText: "",
        scheduler: {},
    });
    const [draftButtonDisabled, setDraftButtonDisabled] = useState(false);
    const [formikProps, setFormikProps] = useState({});
    const [tadWarningPopupVisible, setTadWarningPopupVisible] = useState(false);
    const [tadWarningShowed, setTadWarningShowed] = useState(false);
    const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
    const isMobile = useMediaQuery("min", "md");

    const { debitAccount, creditAccount, amount, reference, email, emailText, sendEmail } = values;

    const extendedAmountVariableEnabled = getBoolean("increase.size.amount.link.iso8583", false);
    const enableHideAmounts = getBoolean("frontend.hide.accounts.balance.funcionalities", false);

    useEffect(() => {
        const isDraftDisabled = !values.debitAccount || !values.creditAccount || !values.amount;
        setDraftButtonDisabled(isDraftDisabled);
    }, [values.debitAccount, values.creditAccount, values.amount]);

    const getActivityIds = () => {
        const cvuCoelsaEnabled = configUtils.getBoolean(
            "frontend.show.transfers.services.Coelsa.CVU.functionalities",
            false,
        );
        if (isUxTransfersOwnImprovementsEnabled() && cvuCoelsaEnabled) {
            return ["transfers.internal.send", "transfersCVU.internal.send"];
        }
        return ["transfers.internal.send"];
    };

    const getActivityId = () => {
        const isEnableCVUCoelsaTransfer = configUtils.getBoolean(
            "frontend.show.transfers.services.Coelsa.CVU.functionalities",
        );
        const ownUxEnabled = isUxTransfersOwnImprovementsEnabled();
        const resolvedCredit = resolveInternalCreditAccount(
            values.creditAccount,
            accounts,
            externalOwnAccounts,
            ownUxEnabled,
        );
        return resolveInternalSendActivityId({
            ownUxEnabled,
            cvuCoelsaEnabled: isEnableCVUCoelsaTransfer,
            reduxIsCvu: isCVU,
            creditAccount: resolvedCredit || values.creditAccount,
        });
    };

    useEffect(() => {
        const { debitAccount: debitAccountDraft } = values;
        dispatch(transferActions.preActivity());
        dispatch(holidaysActions.listHolidays());
        // eslint-disable-next-line no-shadow
        const { cbu, email, transferKind, isFrequentDestinations } = summary;

        if (location?.pathname === "/v2/transfer/transfer") {
            saveLocation(props);
            if (location?.state) {
                // eslint-disable-next-line no-shadow
                const { isInternal, debitAccount } = location?.state;
                dispatch(transferActions.changeTransferGroup(isInternal));
                setValues({
                    ...values,
                    debitAccount,
                    creditAccount: "",
                    amount: shape({
                        amount: 0,
                        currency: "",
                    }),
                    cbuOrAlias: "",
                    reference: "",
                    email: "",
                    emailText: "",
                    concept: "VAR",
                    transferKind: "",
                });
                dispatch(transferActions.accountLimitsListActivities(debitAccount, getActivityIds()));
            } else {
                const { activeEnvironment } = props;
                const isInternalTrx = transferKind === "other" ? false : activeEnvironment.permissions.transferInternal;

                if (!isInternalTrx && isFrequentDestinations) {
                    if (cbu) {
                        setValues({
                            ...values,
                            cbuOrAlias: cbu,
                            email,
                            transferKind,
                        });
                    }
                } else if (email && isFrequentDestinations) {
                    setValues({
                        ...values,
                        email,
                    });
                }
                dispatch(transferActions.changeTransferGroup(isInternalTrx));
            }
        } else if (location?.pathname === "/v2/transfer/internal/modify" && debitAccountDraft) {
            dispatch(transferActions.accountLimitsListActivities(debitAccountDraft, getActivityIds()));
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        dispatch(templateActions.loadTemplateList(ID_FORM));
    }, [dispatch]);

    useEffect(() => {
        const {
            debitAccount: summaryDebitAccount,
            creditAccount: summaryCreditAccount,
            reference: summaryReference,
            amount: summaryAmount,
            email: summaryEmail,
            emailText: summaryEmailText,
            sendEmail: summarySendEmail,
            scheduler,
        } = summary;
        const resolvedDebit = resolveTransferAccountRef(summaryDebitAccount);
        const resolvedCredit = sanitizeInternalCreditWhenOwnDisabled(
            resolveTransferAccountRef(summaryCreditAccount),
            isUxTransfersOwnImprovementsEnabled(),
        );

        if (resolvedDebit && resolvedDebit !== debitAccount) {
            dispatch(transferActions.accountLimitsListActivities(resolvedDebit, getActivityIds()));
        }

        setValues((prev) => ({
            ...prev,
            ...(resolvedDebit ? { debitAccount: resolvedDebit } : {}),
            creditAccount: resolvedCredit || "",
            reference: summaryReference || "",
            amount: summaryAmount || "",
            email: summaryEmail || "",
            emailText: summaryEmailText || "",
            sendEmail: summarySendEmail,
            scheduler,
        }));
        // eslint-disable-next-line
    }, [summary]);

    const parseAmount = (value) => {
        if (typeof value === "string") {
            return parseFloat(value.replaceAll(".", "").replace(",", "."));
        }
        return value;
    };

    const isTad = (amountWithCurrency) => {
        const { isRetailEnvironment, activeEnvironment } = props;
        const value = amountWithCurrency.amount;
        const environmentTypeTag = isRetailEnvironment ? "Retail" : "Corporate";
        const currencyTag = amountWithCurrency.currency.toString() === "0" ? "Pesos" : "Dolares";
        const minAmount = configUtils.getInteger(`frontend.TAD.minAmount${currencyTag}.${environmentTypeTag}`, 100000);
        const maxAmount = configUtils.getInteger(`frontend.TAD.maxAmount${currencyTag}.${environmentTypeTag}`, 1000000);

        if (isCVU) {
            return false;
        }

        if (activeEnvironment?.permissions?.transferDiferredAccreditation) {
            if (value >= minAmount && value <= maxAmount) {
                return true;
            }
        }
        return false;
    };

    const setHint = (value) => {
        const isTadTrx = isTad(value);
        if (isTadTrx) {
            setTadWarningPopupVisible(true);
            if (agendaVisible) {
                setAgendaVisible(false);
            }
            if (!tadWarningShowed) {
                setTadWarningShowed(true);
            }
        } else {
            setTadWarningPopupVisible(false);
            setTadWarningShowed(false);
            setAgendaVisible(true);
        }
    };

    useEffect(() => {
        if (amount && debitAccount) {
            const parsedAmount = parseAmount(amount);
            const foundAccount = accounts?.find((account) => account.idProduct === debitAccount);
            if (foundAccount) {
                const { currency } = foundAccount;
                if (parsedAmount > 0) {
                    setHint({ amount: parsedAmount, currency });
                }
            }
        }
        // eslint-disable-next-line
    }, [amount, debitAccount]);

    useEffect(() => {
        if (creditAccount && creditAccount === debitAccount) {
            setValues((prev) => ({
                ...prev,
                creditAccount: undefined,
            }));
            return;
        }
        const debit = accounts.find((account) => account.idProduct === debitAccount);
        if (creditAccount && debit?.cbu && creditAccount === debit.cbu) {
            setValues((prev) => ({
                ...prev,
                creditAccount: undefined,
            }));
        }
        // eslint-disable-next-line
    }, [creditAccount, debitAccount, accounts]);

    const selectedAccount = () => accounts.find((account) => account.idProduct === values.debitAccount);
    const getDefaultAccount = () => accounts?.find((account) => account.favorite)?.idProduct || accounts[0]?.idProduct;

    useEffect(() => {
        if (!values.debitAccount || selectedAccount()) {
            return;
        }
        const defaultAccount = getDefaultAccount();
        if (defaultAccount && defaultAccount !== values.debitAccount) {
            setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
            dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
        }
    }, [values.debitAccount, accounts]);

    const isValidAlias = (value) => {
        /* Regex that accept letter from a to z including capital letters, (.), (-), numbers
        and excluding (Ñ) and (ñ) */
        const reg = /^(?:(?![ñÑ])[a-zA-Z.\-\d])+$/;
        return reg.test(value);
    };

    const isValidCbu = (value) => {
        const reg = /^\d+$/;
        return reg.test(value) && value.length === parseInt(MAX_REQUIRED, 10);
    };

    // VERIFICAR EN ONBLUR / OnChange de cbu/alias
    const verifyCvu = (value) => {
        if (isValidCbu(value)) {
            const isACVU = value.startsWith("000");
            dispatch(transferActions.setIsCVU(isACVU));
        } else if (isValidAlias(value)) {
            dispatch(transferActions.getCbuCvuFromAlias(value));
        }
    };

    const changeCbu = (record) => {
        const transferKindDestination = record.isOwn === "1" ? "same" : "other";
        setValues({
            ...values,
            cbuOrAlias: record.account,
            transferKind: transferKindDestination,
            email: record.email,
        });
        verifyCvu(record.account);
    };

    const closeTADModal = () => {
        setTadWarningPopupVisible(false);
    };

    const getActivityIdTad = () => "transfers.internal.tad.send";

    const handleDebitAccountSelect = (accountId) => {
        setValues((prev) => ({ ...prev, debitAccount: accountId }));
        dispatch(transferActions.accountLimitsListActivities(accountId, getActivityIds()));
    };

    const selectedCurrency = () => {
        const selectedAccountForLabel = accounts.find((account) => account.idProduct === values.debitAccount);
        return selectedAccountForLabel?.currency ? i18n.get(`currency.label.${selectedAccountForLabel?.currency}`) : "";
    };

    const mapAccounts = useMemo(
        () =>
            accounts.map((account) => ({
                accountName: account.productAlias || account.shortLabel,
                amount: account.balance,
                currency: account.currency ? i18n.get(`currency.label.${account.currency}`) : "",
                description: account.productType ? i18n.get(`productType.${account.productType}`) : "",
                id: account.idProduct,
                codeBank: account.cbu?.substring(0, 3) ?? "319",
            })),
        [accounts],
    );

    const creditAccountOptions = useMemo(() => {
        const debit = accounts.find((account) => account.idProduct === values.debitAccount);
        return buildInternalCreditAccountOptions({
            cmfOptions: mapAccounts,
            externalOwnAccounts,
            debitAccountId: values.debitAccount,
            debitCurrencyLabel: selectedCurrency(),
            debitCbu: debit?.cbu,
            getCurrencyLabel: (code) => i18n.get(`currency.label.${code}`),
            ownUxEnabled: isUxTransfersOwnImprovementsEnabled(),
        });
    }, [accounts, externalOwnAccounts, mapAccounts, values.debitAccount]);

    useEffect(() => {
        if (values.debitAccount) {
            return;
        }
        const { debitAccount: summaryDebitAccount } = summary;
        let defaultAccount;
        if (summaryDebitAccount) {
            defaultAccount = summaryDebitAccount.value || summaryDebitAccount.idProduct || summaryDebitAccount;
        } else {
            defaultAccount = accounts?.find((account) => account.favorite)?.idProduct || accounts[0]?.idProduct;
        }
        if (defaultAccount) {
            setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
            dispatch(transferActions.accountLimitsListActivities(defaultAccount, getActivityIds()));
        }
    }, [accounts, summary, values.debitAccount]);

    useEffect(() => {
        if (!formikProps?.setValues || !values.debitAccount || fetchingSubmit) {
            return;
        }
        formikProps.setValues({
            debitAccount,
            creditAccount,
            amount,
            reference,
            sendEmail,
            email,
            emailText,
        });
    }, [
        formikProps,
        values.debitAccount,
        fetchingSubmit,
        debitAccount,
        creditAccount,
        amount,
        reference,
        sendEmail,
        email,
        emailText,
    ]);

    const handleCreateTemplate = (propsFormik) => {
        const { currency } = accounts.find((account) => account.idProduct === values.debitAccount);
        onCreateTemplate({ ...propsFormik.values, currency });
    };

    const handleChangeAmount = (a) => {
        setValues({ ...values, amount: a });
    };

    const referenceMaxLength = configUtils.getInteger("frontend.transfers.reference.maxLength", 12);
    const idActivity = getActivityId();

    const handleTemplateSelect = (template) => {
        const a = template.amount?.amount || 0;
        const amountWithCurrency = template.amount;
        const isTadTrx = isTad(amountWithCurrency);
        const safeCredit = sanitizeInternalCreditWhenOwnDisabled(
            template.creditAccount,
            isUxTransfersOwnImprovementsEnabled(),
        );
        setIsTemplatesVisible(false);
        setValues({
            ...values,
            debitAccount: template.debitAccount,
            creditAccount: safeCredit || "",
            transferKind: template.transferKind,
            cbuOrAlias: template.cbuOrAlias,
            amount: a,
            concept: template.concept,
            reference: template.reference,
            sendEmail: template.sendEmail,
            emailText: template.emailText,
            email: template.email,
            flagHint: isTadTrx,
            tadTrx: isTadTrx,
        });

        dispatch(transactionLinesActions.loadTransactionLinesTemplateData(template));
        // eslint-disable-next-line no-shadow
        const { debitAccount } = template;
        if (debitAccount) {
            dispatch(transferActions.accountLimitsListActivities(debitAccount, getActivityIds()));
        }
    };

    const getDraftSummary = () => {
        const { idTransaction } = props;
        /* eslint-disable no-shadow */
        const {
            debitAccount,
            creditAccount,
            amount: { amount, currency },
            reference,
            sendEmail,
            email,
            emailText,
            tadTrx,
            scheduler,
            flagHint,
        } = values;
        /* eslint-disable no-shadow */
        const resolvedDebit = resolveTransferAccountRef(debitAccount) || debitAccount;
        const resolvedCredit = resolveTransferAccountRef(creditAccount) || creditAccount;
        const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === resolvedDebit);
        const schedulerToSend = scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? scheduler : null;
        const completeCreditAccount = resolveInternalCreditAccount(
            resolvedCredit,
            accounts,
            externalOwnAccounts,
            isUxTransfersOwnImprovementsEnabled(),
        );
        return {
            debitAccount: completeDebitAccount,
            creditAccount: completeCreditAccount,
            currency,
            amount,
            reference,
            sendEmail,
            email,
            emailText,
            scheduler: schedulerToSend,
            idTransaction,
            tadTrx,
            flagHint,
        };
    };

    const onSaveDraft = (draftValues) => {
        const { idTransaction } = props;
        // eslint-disable-next-line no-shadow
        const { transferKind, concept, debitAccount } = draftValues;

        // Fallback al estado local si Formik quedó desincronizado.
        const resolvedDebit = resolveTransferAccountRef(debitAccount) || debitAccount || values.debitAccount;
        const resolvedCredit =
            resolveTransferAccountRef(draftValues.creditAccount) || draftValues.creditAccount || values.creditAccount;
        const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === resolvedDebit);
        const parsedAmount = parseAmount(draftValues.amount) || 0;
        const tadTrx = isTad({
            currency: completeDebitAccount.currency,
            amount: parsedAmount,
        });
        dispatch(
            formActions.saveDraft({
                idForm: ID_FORM,
                idActivityDraft: tadTrx ? getActivityIdTad() : getActivityId(),
                data: {
                    ...draftValues,
                    amount: {
                        quantity: parsedAmount,
                        currency: completeDebitAccount.currency,
                    },
                    debitAccount: { value: resolvedDebit },
                    creditAccount: { value: resolvedCredit },
                    transferKind: transferKind?.value,
                    concept: concept?.value,
                },
                idTransaction: idTransaction || null,
                scheduler: draftValues?.scheduler,
                summary: getDraftSummary(),
            }),
        );
    };
    // eslint-disable-next-line no-shadow
    const onSubmit = ({ debitAccount, creditAccount, amount, reference, sendEmail, email, emailText, flagHint }) => {
        if (tadWarningPopupVisible) {
            return;
        }
        const { scheduler } = values;

        // Resolvemos posibles refs con forma {value|idProduct|...} y hacemos fallback al estado local
        // que es la fuente de verdad que se muestra en la UI (evita desincronizaciones con Formik).
        const resolvedSubmitDebit = resolveTransferAccountRef(debitAccount) || values.debitAccount;
        const resolvedSubmitCredit = resolveTransferAccountRef(creditAccount) || values.creditAccount;

        const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === resolvedSubmitDebit);
        const completeCreditAccount = resolveInternalCreditAccount(
            resolvedSubmitCredit,
            accounts,
            externalOwnAccounts,
            isUxTransfersOwnImprovementsEnabled(),
        );
        const currency = parseInt(completeDebitAccount.currency, 10);

        let schedulerToSend = scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? { ...scheduler } : null;
        if (schedulerToSend) {
            schedulerToSend = { ...schedulerToSend, newValueDate: schedulerToSend.valueDate };
            delete schedulerToSend.valueDate;
        }

        const tadTrx = isTad({
            currency,
            amount: parseAmount(amount),
        });

        if (isDesktop) {
            dispatch(replace("/v2/transfer/internal/back"));
        }
        dispatch(
            transferActions.createInternalTransfer(
                {
                    debitAccount: completeDebitAccount,
                    creditAccount: completeCreditAccount,
                    currency,
                    amount: parseAmount(amount),
                    reference,
                    sendEmail,
                    email,
                    emailText,
                    scheduler: schedulerToSend,
                    idTransaction: summary.idTransaction,
                    tadTrx,
                    flagHint,
                },
                null, // formikBag,
            ),
        );
    };

    const handleSchedulerChange = (data) => {
        if (data.frequency === schedulerUtils.ONCE) {
            setValues({
                ...values,
                scheduler: {
                    selectedOption: data.frequency,
                    valueDate: data.startsOn,
                    program: null,
                },
            });
        } else {
            const newProgramData = {
                // one of DAY, WEEK, MONTH, YEAR
                frequency: data.frequency === schedulerUtils.CUSTOM ? data.customFreq : data.frequency,
                // int that represents the frequency, frequency = DAY +  frequencyValue = 2 => every 2 days
                frequencyValue: data.frequency === schedulerUtils.CUSTOM ? data.customFreqValue : "1",
                // int that represents the days of the week used by the java class Days at the backend.
                // Same class used by the calendar restriction
                days: dateUtils.getDayFromDate(data.startsOn),
                day: data.startsOn.getDate(), // day of the month.
                occurrence: null, // occurrence=3 + days=4 every 3rd WEDNESDAY of the month
                startsOn: data.startsOn,
                lapse: data.lapse.lapse, // no change at all
                number: data.lapse.number,
                date: data.lapse.date,
                isCustom: data.frequency === schedulerUtils.CUSTOM,
                selectedOption: data.frequency,
            };
            if (newProgramData.frequency === schedulerUtils.WEEK && newProgramData.isCustom) {
                // if it's custom, I take the days from the bitwisedays control
                newProgramData.days = data.days;
            }
            if (newProgramData.frequency === schedulerUtils.MONTH && data.occurrenceType === "occurrence") {
                newProgramData.occurrence = dateUtils.weekOfMonth(moment(data.startsOn));
                newProgramData.day = null;
            }
            setValues({
                ...values,
                scheduler: {
                    selectedOption: newProgramData.frequency,
                    program: newProgramData,
                },
            });
        }
        setIsScheduleModalVisible(false);
    };

    const customHandleKeyDown = (e) => {
        const { decimalSeparator } = numberFormat(lang);
        const { value, selectionStart, selectionEnd } = e.target;
        const integerPart = value.split(decimalSeparator)[0].replace(/\D/g, "");
        const extendedAmountEnabled = getBoolean("increase.size.amount.link.iso8583", false);
        const maxSize = extendedAmountEnabled ? 15 : 10;

        // Array de teclas permitidas para movimiento, navegación y selección
        const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
            "Home",
            "End",
            "Control",
            "Meta",
            "Shift",
        ];

        // Permitir combinaciones con teclas de control (Ctrl, Cmd, etc.)
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            return; // Permitir combinaciones de teclas como Ctrl+A, Ctrl+C, etc.
        }

        const isAllSelected = selectionStart === 0 && selectionEnd === value.length;

        // Detectar si el cursor está en la parte decimal
        const decimalIndex = value.indexOf(decimalSeparator);
        // Permitir el ingreso del separador decimal cuando se alcanza el número máximo de dígitos
        if (
            (e.key === decimalSeparator || e.key === ".") &&
            (selectionStart <= decimalIndex || decimalIndex === -1) &&
            integerPart.length >= maxSize
        ) {
            return; // Permitir el ingreso del separador decimal
        }

        if (isAllSelected) {
            return; // Permitir el reemplazo al seleccionar todo el texto
        }

        // Si el cursor está en la parte entera y el número ya tiene 10 dígitos
        if (
            integerPart.length >= maxSize &&
            (selectionStart <= decimalIndex || decimalIndex === -1) &&
            !allowedKeys.includes(e.key)
        ) {
            e.preventDefault(); // Bloquea la entrada de más dígitos en la parte entera si se excede el límite
        }
    };

    const inputList = [
        {
            colProps: {
                size: 12,
            },
            component: "accountSelect",
            id: "debitAccount",
            label: i18n.get("transfers.form.debit.account"),
            required: true,
            options: mapAccounts,
            defaultValue: debitAccount,
            value: debitAccount,
            onChange: (value) => handleDebitAccountSelect(value),
            hideAmount: enableHideAmounts && hideAmount && isMobile,
        },
        {
            colProps: {
                size: 12,
            },
            id: "limits",
            customElement: () => (
                <Limits
                    values={values}
                    limit={limit}
                    limits={limits}
                    limitUsed={limitUsed}
                    isDesktop={isDesktop}
                    currency={selectedAccount()?.currency}
                />
            ),
        },
        {
            colProps: {
                size: 12,
            },
            component: "accountSelect",
            id: "creditAccount",
            label: i18n.get("transfers.form.credit.account"),
            required: true,
            value: creditAccount,
            options: creditAccountOptions,
            onChange: (value) => {
                setValues((prev) => ({ ...prev, creditAccount: value }));
                const resolvedCredit = resolveInternalCreditAccount(
                    value,
                    accounts,
                    externalOwnAccounts,
                    isUxTransfersOwnImprovementsEnabled(),
                );
                dispatch(transferActions.setIsCVU(isInternalCreditCvu(resolvedCredit || value)));
            },
            placeholder: i18n.get("transfers.form.credit.account.placeholder"),
            hideAmount: enableHideAmounts && hideAmount && isMobile,
        },
        {
            colProps: {
                size: 12,
            },
            component: "amountField",
            allowNegative: false,
            currency: selectedCurrency(),
            id: "amount",
            label: i18n.get("transfers.form.amount"),
            required: true,
            value: amount,
            onBlur: (e, propsFormik) => handleChangeAmount(propsFormik.values.amount),
            maxLength: extendedAmountVariableEnabled ? undefined : 15,
            extendedAmountEnabled: extendedAmountVariableEnabled,
            onKeyDown: (e) => customHandleKeyDown(e),
            afterElement: tadWarningShowed ? (
                <Box mx={12}>
                    <Button
                        icon="Info"
                        variant="text"
                        onClick={(e) => {
                            e.preventDefault();
                            setTadWarningPopupVisible(true);
                        }}
                    />
                </Box>
            ) : null,
            helper: tadWarningShowed ? (
                <Text color="primary" variant="caption">
                    {configUtils.get("frontend.TAD.warning.text")}
                </Text>
            ) : null,
        },
        {
            colProps: {
                size: 12,
            },
            component: "textField",
            id: "reference",
            value: reference,
            onChange: (e) => {
                setValues({ ...values, reference: e.target.value });
            },
            label: i18n.get("transfers.form.reference"),
            placeholder: i18n.get("transfers.form.reference.placeholder"),
            maxLength: referenceMaxLength,
        },
        {
            colProps: {
                size: 12,
            },
            id: "schedule",
            customElement: () => {
                if (agendaVisible) {
                    return (
                        <Box>
                            <Button
                                text={i18n.get("transfers.form.schedule")}
                                icon="CalendarAdd"
                                variant="solid"
                                id="modal-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsScheduleModalVisible(true);
                                }}
                            />
                            {values?.scheduler?.selectedOption !== undefined && (
                                <Box mt={8}>
                                    <Message value={values.scheduler || { valueDate: "" }} />
                                </Box>
                            )}
                        </Box>
                    );
                }
                return <></>;
            },
        },
        {
            colProps: {
                size: 12,
            },
            component: "checkbox",
            id: "sendEmail",
            label: i18n.get("transfers.form.send.mail"),
            isChecked: sendEmail,
            onChange: () => setValues({ ...values, sendEmail: !values.sendEmail }),
        },
    ];
    if (sendEmail) {
        inputList.push(
            {
                colProps: {
                    size: 12,
                },
                component: "textField",
                id: "email",
                label: "Email",
                maxLength: 50,
                value: email,
                onChange: (e) => {
                    setValues({ ...values, email: e.target.value });
                },
                required: true,
            },
            {
                colProps: {
                    size: 12,
                },
                component: "textField",
                id: "emailText",
                value: emailText,
                onChange: (e) => {
                    setValues({ ...values, emailText: e.target.value });
                },
                label: "Mensaje",
                maxLength: 50,
            },
        );
    }

    if ((fetching && !accounts.length) || !values.debitAccount || fetchingSubmit) {
        return (
            <Box flex justify="center" align="center" column py={64}>
                <Spinner size="large" />
            </Box>
        );
    }

    const initialValues = {
        debitAccount: "",
        creditAccount: "",
        amount: "",
        reference: "",
        sendEmail: false,
        email: "",
        emailText: "",
    };

    return (
        <>
            <SchedulerModal
                isVisible={isScheduleModalVisible}
                onClose={() => setIsScheduleModalVisible(false)}
                onScheduleClick={(data) => handleSchedulerChange(data)}
                scheduler={summary?.scheduler}
            />
            <TADWarningModal
                isVisible={tadWarningPopupVisible}
                onClick={changeCbu}
                closeModal={closeTADModal}
                isDesktop={isDesktop}
            />
            <Box p={24}>
                <Form
                    wrapButtons
                    wrapButtonsFlex
                    buttonSubmit={{
                        colProps: {
                            size: {
                                md: 3,
                                xs: 12,
                            },
                        },
                        text: i18n.get("transfers.form.continue"),
                        variant: "solid",
                    }}
                    initialValues={initialValues}
                    inputsList={inputList}
                    buttonsList={[
                        {
                            colProps: {
                                size: {
                                    xs: 12,
                                    lg: 4,
                                },
                            },
                            w: isMobile || window.innerWidth <= 1010 ? "100%" : 200,
                            component: "button",
                            key: "draft",
                            text: i18n.get("transfers.form.save.draft"),
                            icon: "Save",
                            variant: "outline",
                            type: "button",
                            loading: fetchingDraft,
                            isInactive:
                                (draftButtonDisabled && location?.pathname !== "/v2/transfer/internal") ||
                                fetchingDraft,
                            onClick: (e, propsFormik) => {
                                if (!isUxTransfersImprovementsEnabled()) {
                                    setDraftButtonDisabled(true);
                                }
                                onSaveDraft(propsFormik.values);
                            },
                        },
                        {
                            colProps: {
                                size: {
                                    xs: 12,
                                    lg: 4,
                                },
                            },
                            w: isMobile || window.innerWidth <= 1010 ? "100%" : 200,
                            component: "button",
                            key: "template",
                            text: i18n.get("transfers.form.save.template"),
                            icon: "Save",
                            variant: "outline",
                            type: "button",
                            isInactive: false,
                            id: "modal-button",
                            onClick: (e, propsFormik) => handleCreateTemplate(propsFormik),
                        },
                        {
                            colProps: {
                                size: {
                                    xs: 12,
                                    lg: 4,
                                },
                            },
                            w: isMobile || window.innerWidth <= 1010 ? "100%" : 200,
                            component: "button",
                            key: "submit",
                            text: i18n.get("transfers.form.continue"),
                            icon: "ArrowRight",
                            variant: "solid",
                            iconVariant: "outline",
                            type: "submit",
                            extraInactiveCondition: (formValues) =>
                                isTransferInternalContinueInactive(formValues, { sendEmail }),
                            onClick: () => {},
                        },
                    ]}
                    onSubmit={(v) => onSubmit(v)}
                    setFormikProps={setFormikProps}
                    rowSpacingForm={32}
                    validationSchema={Yup.object().shape({
                        debitAccount: Yup.string().required(i18n.get("transfers.validation.required")),
                        creditAccount: Yup.string().required(i18n.get("transfers.validation.required")),
                        amount: Yup.string()
                            .required(i18n.get("transfers.validation.required"))
                            .test("test", i18n.get("transfers.validation.min.amount"), (a) => parseAmount(a) > 0),
                        sendEmail: Yup.boolean(),
                        email: Yup.string().when("sendEmail", {
                            is: true,
                            then: Yup.string()
                                .required(i18n.get("transfers.validation.required"))
                                .email(i18n.get("transfers.validation.invalid")),
                            otherwise: Yup.string(),
                        }),
                    })}
                />
                <CreateTemplateModal values={values} idActivityTemplate={idActivity} idForm={ID_FORM} />
            </Box>
            <TemplateDrawer
                isVisible={isTemplatesVisible}
                setVisible={setIsTemplatesVisible}
                templates={templates}
                onSelect={handleTemplateSelect}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetching(state),
    fetchingDraft: formSelectors.getFetchingDraft(state),
    fetchingSubmit: transferSelectors.getFetchingSubmit(state),
    accounts: transferSelectors.getAccounts(state),
    externalOwnAccounts: transferSelectors.getExternalOwnAccounts(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    limits: transferSelectors.getLimits(state),
    summary: transferSelectors.getSummary(state),
    templates: templateSelectors.getTemplateList(state),
    isCVU: transferSelectors.getIsCVU(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    lang: i18nSelectors.getLang(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
});

export default withRouter(connect(mapStateToProps)(TransferInternalForm));
